import { useRef } from 'react';
import { ListFilter, X } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { CountBadge } from '@/components/shared/CountBadge';

interface FilterToggleButtonProps {
  /** How many filters are narrowing the list right now. */
  activeCount: number;
  open: boolean;
  onToggle: () => void;
  /**
   * Resets every filter. Renders a trailing × once something is applied; with
   * no handler the button is toggle-only.
   */
  onClear?: () => void;
  /** Id of the drawer this button discloses. */
  controls: string;
  className?: string;
}

/**
 * The permanent handle on the filter drawer, next to the search box.
 *
 * It stays put whether or not anything is applied, so the control never moves
 * out from under the pointer, and it carries the count: with the drawer shut
 * the number is the only thing saying the list is narrowed at all. Once there
 * is a count there is also a × to clear it, in the one place that is on screen
 * whether or not the drawer is open — which is exactly where you look when the
 * list is narrower than you expected.
 */
export function FilterToggleButton({
  activeCount,
  open,
  onToggle,
  onClear,
  controls,
  className,
}: FilterToggleButtonProps) {
  const toggleRef = useRef<HTMLButtonElement>(null);
  const active = activeCount > 0;
  const showClear = active && Boolean(onClear);

  return (
    <div
      className={cn(
        'inline-flex h-7 shrink-0 items-center rounded-md border text-xs transition-colors',
        active
          ? 'border-ring/30 bg-ring/[0.07] text-foreground'
          : 'border-input text-muted-foreground',
        open && (active ? 'bg-ring/[0.12]' : 'bg-accent text-foreground'),
        className,
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={toggleRef}
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={controls}
            aria-label={active ? `Filters, ${activeCount} active` : 'Filters'}
            data-testid="filter-bar-toggle"
            className={cn(
              'flex h-full items-center gap-1 rounded-l-md px-2 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
              active ? 'hover:bg-ring/10' : 'hover:bg-accent hover:text-foreground',
              !showClear && 'rounded-r-md',
            )}
          >
            <ListFilter className="size-3.5 shrink-0" aria-hidden="true" />
            {active && <CountBadge count={activeCount} data-testid="filter-bar-count" />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{open ? 'Hide filters' : 'Filters'}</TooltipContent>
      </Tooltip>

      {showClear && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => {
                onClear?.();
                // The × goes away with the last filter, so hand focus to the
                // half of the control that stays.
                requestAnimationFrame(() => toggleRef.current?.focus());
              }}
              aria-label="Clear all filters"
              data-testid="filter-bar-clear"
              className={cn(
                'flex h-full items-center rounded-r-md border-l border-ring/20 px-1.5 text-muted-foreground transition-colors',
                'hover:bg-destructive/10 hover:text-destructive',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
              )}
            >
              <X className="size-3" aria-hidden="true" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Clear all filters</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
